import React, { FormEvent, useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';

import { Alert, Box, Button, Stack } from '@mui/material';

import CorporateAirAgreementItem from '~/components/LeBusinessTraveller/components/CorporateAirAgreementItem';
import FrequentFlyerItem from '~/components/LeBusinessTraveller/components/FrequentFlyerItem';

import {
  VendorAgreementUpdateRequest,
  getVendorAgreements,
  updateVendorAgreements,
} from '~/services/BusinessTraveller/BusinessTravellerService';

export interface FrequentFlyerItemType {
  id?: string;
  code: string;
  airlineName: string;
  verified: boolean;
  isCorporateAgreement: boolean;
}

export interface VendorAgreementItemType {
  id: string;
  vendorCode: string;
  vendorName: string;
  corporateCode: string;
  identityCode: string;
  osi: string;
  ssrCode: string;
  ssrMessage: string;
  verified: boolean;
  isCorporateAgreement: boolean;
}

// Virgin's program has one code, while Qantas's code is picked by its level
const VIRGIN_PROGRAM_CORPORATE_CODE = 'ACC99';

// LE's programs' identity codes, as in the FDA CorporateCodes table
const LeProgramIdentityCodeMap = {
  QFB01: 'QFB01',
  QFB02: 'QFB02',
  QFB03: 'QFB03',
  ACC99: '079016',
};

function toUpdateItem(item: VendorAgreementItemType): VendorAgreementUpdateRequest[number] {
  // A pending agreement has no codes, OSI or SSR, whatever was typed in
  if (!item.verified) {
    return {
      id: item.id,
      verified: false,
      corporateCode: null,
      identityCode: null,
      osi: null,
      ssrCode: null,
      ssrMessage: null,
    };
  }

  const isVirginProgram = !item.isCorporateAgreement && item.vendorName === 'virgin';
  const corporateCode = isVirginProgram ? VIRGIN_PROGRAM_CORPORATE_CODE : item.corporateCode.trim() || null;
  const identityCode = item.isCorporateAgreement
    ? item.identityCode.trim() || null
    : (LeProgramIdentityCodeMap[corporateCode] ?? null);

  return {
    id: item.id,
    verified: true,
    corporateCode,
    identityCode,
    osi: item.osi.trim() || null,
    ssrCode: item.ssrCode.trim() || null,
    ssrMessage: item.ssrMessage.trim() || null,
  };
}

interface Params {
  businessId: string;
  tab: string;
}

export default function FrequentFlyerTab() {
  const [frequentFlyerItems, setFrequentFlyerItems] = useState<Array<VendorAgreementItemType>>([]);
  const [corporateAirAgreementItems, setCorporateAirAgreementItems] = useState<Array<VendorAgreementItemType>>([]);
  const [fetchFrequentFlyerError, setFetchFrequentFlyerError] = useState(false);
  const [saveError, setSaveError] = useState<string>();
  const [hasSaved, setHasSaved] = useState(false);
  const { businessId } = useParams<keyof Params & string>();

  async function fetchFrequentFlyer() {
    try {
      const result = await getVendorAgreements(businessId);

      const vendorAgreements = result.result.map((item) => ({
        id: item.id,
        vendorCode: item.vendorCode,
        vendorName: item.vendorName,
        corporateCode: item.corporateCode ?? '',
        identityCode: item.identityCode ?? '',
        osi: item.osi ?? '',
        ssrCode: item.ssrCode ?? '',
        ssrMessage: item.ssrMessage ?? '',
        verified: item.verified,
        isCorporateAgreement: item.isCorporateAgreement,
      }));

      // Separate and set frequentFlyerItems and corporateAirAgreementItems
      const frequentFlyers = vendorAgreements.filter((item) => !item.isCorporateAgreement);
      const corporateAgreements = vendorAgreements.filter((item) => item.isCorporateAgreement);

      setFrequentFlyerItems(frequentFlyers);
      setCorporateAirAgreementItems(corporateAgreements);
    } catch (e) {
      setFetchFrequentFlyerError(true);
      console.error(e);
    }
  }

  useEffect(() => {
    fetchFrequentFlyer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setHasSaved(false);

    const combinedFrequentFlyerItems = [...frequentFlyerItems, ...corporateAirAgreementItems];

    if (combinedFrequentFlyerItems.length === 0) {
      return;
    }

    try {
      await updateVendorAgreements(combinedFrequentFlyerItems.map(toUpdateItem), businessId);
      setSaveError(undefined);
    } catch (e) {
      setSaveError(e.message);
      return;
    }

    setHasSaved(true);
    await fetchFrequentFlyer();
  }

  return (
    <form onSubmit={onSubmit}>
      <Stack gap={4} sx={{ wordWrap: 'anywhere' }}>
        {fetchFrequentFlyerError && <Alert severity="error">Error fetching frequent flyer</Alert>}
        {saveError && <Alert severity="error">Error saving frequent flyer: {saveError}</Alert>}
        {corporateAirAgreementItems.map((item, index) => (
          <CorporateAirAgreementItem
            key={index}
            businessId={businessId}
            item={item}
            index={index}
            frequentFlyerItems={corporateAirAgreementItems}
            setFrequentFlyerItems={setCorporateAirAgreementItems}
            setHasSaved={setHasSaved}
          />
        ))}
        {frequentFlyerItems.map((item, index) => (
          <FrequentFlyerItem
            key={index}
            businessId={businessId}
            item={item}
            index={index}
            frequentFlyerItems={frequentFlyerItems}
            setFrequentFlyerItems={setFrequentFlyerItems}
            setHasSaved={setHasSaved}
          />
        ))}
        <Box display="flex" justifyContent="flex-start">
          <Button type="submit" variant={hasSaved ? 'outlined' : 'contained'}>
            {(hasSaved && 'Saved') || 'Save'}
          </Button>
        </Box>
      </Stack>
    </form>
  );
}
